import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BuildingsIcon,
    DotsThreeIcon,
    FolderIcon,
    FolderOpenIcon,
    FolderUserIcon,
    PencilSimpleIcon,
    TrashIcon,
} from '@phosphor-icons/react';
import { Badge } from '@/components/Badge';
import { ListItem } from '@/components/List';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItemBase,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { SourceFolder } from '@/types/source-folder';
import { Permission } from '@/types/permissions';
import { usePermissions } from '@/hooks';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';

interface FolderRowProps {
    folder: SourceFolder;
    selected: boolean;
    onSelect: () => void;
    onEditFolder?: (folder: SourceFolder) => void;
    onDeleteFolder?: (folder: SourceFolder) => void;
}

export function FolderRow({
    folder,
    selected,
    onSelect,
    onEditFolder,
    onDeleteFolder,
}: FolderRowProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const { canPerform } = usePermissions();
    const [menuOpen, setMenuOpen] = useState(false);

    const canEdit =
        !!onEditFolder && canPerform(Permission.EDIT, folder.isOwner);
    const canDelete =
        !!onDeleteFolder && canPerform(Permission.DELETE, folder.isOwner);
    const hasMenu = canEdit || canDelete;

    const Icon = !folder.isOwner
        ? FolderUserIcon
        : selected
          ? FolderOpenIcon
          : FolderIcon;

    const isSharedByOwner = folder.isOwner && folder.isShared;

    return (
        <ListItem
            variant="short"
            title={
                isSharedByOwner ? (
                    <span className="flex min-w-0 items-center gap-1.5">
                        <span className="min-w-0 truncate">{folder.name}</span>
                        <Badge
                            variant="iconSquare"
                            className={cn(
                                'shrink-0',
                                selected && 'bg-grey-500',
                            )}
                        >
                            <BuildingsIcon
                                className="size-3 text-grey-200"
                                weight="fill"
                                aria-label={t('folders.sharedBadge')}
                            />
                        </Badge>
                    </span>
                ) : (
                    folder.name
                )
            }
            selected={selected}
            onClick={onSelect}
            data-testid={`folder-sidebar-item-${folder.id}`}
            startContent={
                <Icon
                    className={cn(
                        'size-3.5',
                        selected ? 'text-white-100' : 'text-grey-200',
                    )}
                    aria-hidden
                />
            }
            endContent={
                <>
                    <Badge
                        variant={selected ? 'number' : 'numberDark'}
                        className={cn(
                            menuOpen
                                ? 'hidden'
                                : hasMenu
                                  ? 'group-hover:hidden'
                                  : undefined,
                        )}
                    >
                        {folder.sourceCount ?? 0}
                    </Badge>
                    {hasMenu && (
                        <DropdownMenu
                            open={menuOpen}
                            onOpenChange={setMenuOpen}
                        >
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    aria-label={t('folders.folderActions')}
                                    onClick={(event) => event.stopPropagation()}
                                    className={cn(
                                        'flex size-4 cursor-pointer items-center justify-center rounded text-grey-200 transition-colors hover:bg-grey-500 hover:text-white-100',
                                        menuOpen
                                            ? 'flex'
                                            : 'hidden group-hover:flex',
                                    )}
                                    data-testid={`folder-sidebar-menu-${folder.id}`}
                                >
                                    <DotsThreeIcon
                                        className="size-3.5"
                                        weight="bold"
                                    />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                sideOffset={4}
                                className="rounded"
                            >
                                {canEdit && onEditFolder && (
                                    <DropdownMenuItemBase
                                        onSelect={() => onEditFolder(folder)}
                                    >
                                        <PencilSimpleIcon className="size-4" />
                                        {t('folders.editFolder')}
                                    </DropdownMenuItemBase>
                                )}
                                {canDelete && onDeleteFolder && (
                                    <DropdownMenuItemBase
                                        variant="danger"
                                        onSelect={() => onDeleteFolder(folder)}
                                    >
                                        <TrashIcon className="size-4" />
                                        {t('folders.deleteFolder')}
                                    </DropdownMenuItemBase>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </>
            }
        />
    );
}
